import { inject, Injectable } from "@angular/core";
import { SectorsService, ThematicCreateDto, ThematicListDto, ThematicsService, ThematicUpdateDto } from "app/common/generated";
import { DialogService } from "app/common/services/dialog.service";
import { SnackbarService } from "app/common/services/snackbar.service";
import { catchError, map, Observable, of, switchMap } from "rxjs";
import { ThematicCreateComponent } from "./thematic-create/thematic-create.component";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { ThematicUpdateComponent } from "./thematic-update/thematic-update.component";

@Injectable({
    providedIn: 'root'
})
export class ThematicAppService {
    private readonly apiService = inject(ThematicsService);
    private readonly sectorApiService = inject(SectorsService);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly confirmationService = inject(FuseConfirmationService);

    public getAll(): Observable<ThematicListDto[]> {
        return this.apiService.getAllThematics().pipe(
            map(thematics => thematics.sort((a, b) => a.name?.localeCompare(b.name || '') || 0)),
            catchError((error) => {
                console.error('Error fetching users:', error);
                return of([]);
            })
        );
    }

    public getThematicsBySectorId(sectorId: string): Observable<ThematicListDto[]> {
        return this.apiService.getThematicsBySectorId(sectorId).pipe(
            map(thematics => thematics.sort((a, b) => a.name?.localeCompare(b.name || '') || 0)),
            catchError((error) => {
                console.error('Error fetching thematics by sector ID:', error);
                return of([]);
            })
        );
    }

    public create(subSectorId?: string): Observable<boolean> {

        return this.dialogService.openDialog(ThematicCreateComponent, { subSectorId }).pipe(
            catchError((error) => {
                console.error('Error in thematic create dialog:', error);
                return of(null);
            }),
            switchMap((result: ThematicCreateDto | null) => {
                if (result) {
                    return this.apiService.createThematic(result).pipe(
                        map(() => {
                            this.snackbarService.success('thematic.created');
                            return true;
                        }),
                        catchError((error) => {
                            console.error('Error creating thematic:', error);
                            this.snackbarService.error('thematic.messages.create-error');
                            return of(false);
                        })
                    );
                }

                return of(false);
            })
        );

    }

    public update(thematicId: string): Observable<boolean> {
        return this.apiService.getThematicForUpdate(thematicId).pipe(
            switchMap((thematicToUpdate) => {
                return this.dialogService.openDialog(ThematicUpdateComponent, { thematic: thematicToUpdate }).pipe(
                    catchError((error) => {
                        console.error('Error in thematic update dialog:', error);
                        return of(null);
                    }),
                    switchMap((result: ThematicUpdateDto | null) => {
                        if (!result) {
                            return of(false);
                        }
                        return this.apiService.updateThematic({
                            ...result,
                            id: thematicId
                        }).pipe(
                            map(() => {
                                this.snackbarService.success('thematic.updated');
                                return true;
                            }),
                            catchError((error) => {
                                console.error('Error updating thematic:', error);
                                this.snackbarService.error('thematic.messages.update-error');
                                return of(false);
                            })
                        );
                    })
                );
            })
        );
    }

    public delete(thematicId: string): Observable<boolean> {
        const confirmation = this.confirmationService.open({
            title: 'confirmation',
            message: 'thematic.delete-confirm',
            icon: {
                name: 'heroicons_outline:exclamation-triangle',
                color: 'warn'
            },
            actions: {
                confirm: { show: true, label: 'confirm', color: 'warn' },
                cancel: { show: true, label: 'cancel' }
            },
            dismissible: true
        });

        return confirmation.afterClosed().pipe(
            switchMap((result) => {
                if (result === 'confirmed') {
                    return this.apiService.deleteThematic(thematicId).pipe(
                        map(() => {
                            this.snackbarService.success('thematic.deleted');
                            return true;
                        }),
                        catchError((error) => {
                            console.error('Error deleting thematic:', error);
                            this.snackbarService.error('thematic.messages.delete-error');
                            return of(false);
                        })
                    );
                }
                return of(false);
            })
        );
    }
}